import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { waybillAPI } from '../../api/waybillAPI';

const BUCKET_LABELS = {
  '0-7':   '0–7 days',
  '8-15':  '8–15 days',
  '16-30': '16–30 days',
  '31+':   '31+ days',
};

const PRESET_LABELS = {
  THIS_MONTH:   'This Month',
  LAST_MONTH:   'Last Month',
  THIS_QUARTER: 'This Quarter',
  YTD:          'Year to Date',
  FY:           'Fiscal Year',
};

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-PH', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
};

const fmtLong = (dateStr) =>
  new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-PH', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

// Standard printed-on timestamp — same en-PH long-date + time convention used
// throughout the app (see AuditTimeline.jsx), so every printed report reads
// as the same product.
const printedOnLabel = () =>
  new Date().toLocaleString('en-PH', {
    month: 'long', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });

const emptyData = { rows: [], agingSummary: [], pullOutAgingSummary: [], noPullOutGroup: { count: 0, amount: 0 }, totalCount: 0 };

const PHP = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function UndeliveredFreightForwardingPrintPage() {
  const [searchParams] = useSearchParams();
  const dateFrom = searchParams.get('dateFrom') || '';
  const dateTo = searchParams.get('dateTo') || '';
  const preset = searchParams.get('preset') || '';
  const wbNo = searchParams.get('wbNo') || '';
  const consignee = searchParams.get('consignee') || '';
  const referenceNo = searchParams.get('referenceNo') || '';
  const sealNo = searchParams.get('sealNo') || '';
  const minDays = searchParams.get('minDays') || '';
  const pullOutStatus = searchParams.get('pullOutStatus') || 'all';

  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const printedOn = useMemo(() => printedOnLabel(), []);

  const companyName = useMemo(() => {
    return localStorage.getItem('accountingClientName') || 'ARVANNAH RENZ TRUCKING INC.';
  }, []);

  useEffect(() => {
    waybillAPI.getUndeliveredFreightForwarding({
      pullOutStatus,
      ...(dateFrom     ? { dateFrom }     : {}),
      ...(dateTo       ? { dateTo }       : {}),
      ...(wbNo         ? { wbNo }         : {}),
      ...(consignee    ? { consignee }    : {}),
      ...(referenceNo  ? { referenceNo }  : {}),
      ...(sealNo       ? { sealNo }       : {}),
      ...(minDays      ? { minDays }      : {}),
    })
      .then((res) => {
        setData(res.data?.data || emptyData);
        setLoading(false);
        setTimeout(() => window.print(), 400);
      })
      .catch(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    document.title = 'Undelivered Freight Forwarding Report';
  }, []);

  const summaryByBucket = useMemo(() => {
    const map = {};
    (data.agingSummary || []).forEach((s) => { map[s.bucket] = s.count; });
    return map;
  }, [data.agingSummary]);

  const pullOutSummaryByBucket = useMemo(() => {
    const map = {};
    (data.pullOutAgingSummary || []).forEach((s) => { map[s.bucket] = s; });
    return map;
  }, [data.pullOutAgingSummary]);

  const rangeLabel = dateFrom && dateTo
    ? (dateFrom === dateTo ? fmtLong(dateFrom) : `${fmtLong(dateFrom)} – ${fmtLong(dateTo)}`)
    : '';
  const presetLabel = PRESET_LABELS[preset];
  const periodLabel = presetLabel ? `${presetLabel} (${rangeLabel})` : (rangeLabel || 'All dates');

  if (loading) return <p style={{ padding: 40 }}>Loading report…</p>;

  return (
    <>
      <style>{`
        body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        @page { size: A4 landscape; margin: 12mm; }
        @media print { .no-print { display: none !important; } }
        .page { display: block; }
        .report-header { display: block; text-align: center; margin-bottom: 10px; break-inside: avoid; }
        h1 { font-size: 14pt; margin: 0; }
        h2 { font-size: 11pt; margin: 2px 0; font-weight: normal; }
        .summary { display: block; margin-bottom: 10px; font-size: 9.5pt; break-inside: avoid; }
        .summary span { margin-right: 20px; }
        table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
        th { background: #f0f0f0; border: 1px solid #ddd; padding: 4px 8px; text-align: left; font-size: 9pt; }
        td { border: 1px solid #ddd; padding: 4px 8px; }
        tr:nth-child(even) { background: #fafafa; }
      `}</style>

      <div className="page">
        <div className="report-header">
          <h1>{companyName}</h1>
          <h2>UNDELIVERED FREIGHT FORWARDING REPORT</h2>
          <div style={{ fontSize: '9pt' }}>Waybill date: {periodLabel}</div>
          {wbNo && <div style={{ fontSize: '9pt' }}>Waybill No.: {wbNo}</div>}
          {consignee && <div style={{ fontSize: '9pt' }}>Consignee: {consignee}</div>}
          {referenceNo && <div style={{ fontSize: '9pt' }}>Reference No.: {referenceNo}</div>}
          {sealNo && <div style={{ fontSize: '9pt' }}>Seal No.: {sealNo}</div>}
          {minDays && <div style={{ fontSize: '9pt' }}>Min. Age (Pull-Out): {minDays}</div>}
          <div style={{ fontSize: '8.5pt', marginTop: 4, color: '#555' }}>Printed on {printedOn}</div>
        </div>

        <div className="summary">
          <span>Total Waybills: <strong>{data.totalCount || 0}</strong></span>
          {['0-7', '8-15', '16-30', '31+'].map((bucket) => (
            <span key={bucket}>{BUCKET_LABELS[bucket]}: <strong>{summaryByBucket[bucket] || 0}</strong></span>
          ))}
        </div>

        <div className="summary">
          <span>Pull-Out Aging —</span>
          {['0-7', '8-15', '16-30', '31+'].map((bucket) => (
            <span key={bucket}>
              {BUCKET_LABELS[bucket]}: <strong>{pullOutSummaryByBucket[bucket]?.count || 0}</strong> (₱{PHP(pullOutSummaryByBucket[bucket]?.amount || 0)})
            </span>
          ))}
          <span>No Pull-Out Date: <strong>{data.noPullOutGroup?.count || 0}</strong> (₱{PHP(data.noPullOutGroup?.amount || 0)})</span>
        </div>

        <table>
          <thead>
            <tr>
              <th>WAYBILL NO.</th>
              <th>WAYBILL DATE</th>
              <th>CLIENT/CONSIGNEE</th>
              <th>ROUTE/DETAILS</th>
              <th>REFERENCE NO.</th>
              <th>SEAL NO.</th>
              <th>AGING BUCKET</th>
              <th>PULL-OUT DATE</th>
              <th style={{ textAlign: 'right' }}>AGE (PULL-OUT)</th>
              <th style={{ textAlign: 'right' }}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: 16 }}>No records found.</td>
              </tr>
            ) : (
              (data.rows || []).map((row) => (
                <tr key={row.waybillId}>
                  <td>{row.waybillNo}</td>
                  <td>{formatDate(row.waybillDate)}</td>
                  <td>{row.consignee}</td>
                  <td>{row.route}</td>
                  <td>{row.referenceNumber || ''}</td>
                  <td>{row.sealNo || ''}</td>
                  <td>{BUCKET_LABELS[row.agingBucket] || row.agingBucket}</td>
                  <td>{row.pullOutDate ? formatDate(row.pullOutDate) : ''}</td>
                  <td style={{ textAlign: 'right' }}>{row.ageDays ?? ''}</td>
                  <td style={{ textAlign: 'right' }}>₱{PHP(row.amount || 0)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="no-print" style={{ marginTop: 16 }}>
        <button onClick={() => window.print()} style={{ padding: '6px 16px' }}>Print</button>
      </div>
    </>
  );
}
